import { z } from "zod";

export type PasswordRuleKey = "length" | "uppercase" | "lowercase" | "number";

export interface PasswordRuleMessage {
  label: string;
  error: string;
}

export interface PasswordPolicyMessages {
  rules: Record<PasswordRuleKey, PasswordRuleMessage>;
  summary: string;
}

export const DEFAULT_PASSWORD_POLICY_MESSAGES: PasswordPolicyMessages = {
  rules: {
    length: {
      label: "At least 8 characters",
      error: "Password must be at least 8 characters",
    },
    uppercase: {
      label: "One uppercase letter (A–Z)",
      error: "Password must contain at least one uppercase letter",
    },
    lowercase: {
      label: "One lowercase letter (a–z)",
      error: "Password must contain at least one lowercase letter",
    },
    number: {
      label: "One number (0–9)",
      error: "Password must contain at least one number",
    },
  },
  summary:
    "Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.",
};

let activeMessages: PasswordPolicyMessages = DEFAULT_PASSWORD_POLICY_MESSAGES;

/**
 * Replace the app-wide password policy copy. This is a process-global
 * setting and is intended to be called once during application
 * bootstrap (e.g. by the future i18n initializer on the client, or by
 * server startup when the deployment is single-locale).
 *
 * Do NOT call this from per-request handlers on the server — module
 * state is shared across all in-flight requests and would leak one
 * user's locale onto another user's response. For per-request
 * locale-aware resolution, pass an explicit `messages` argument to
 * `getPasswordRules`, `getPasswordPolicySummary`, or
 * `buildPasswordPolicySchema` instead.
 */
export function setPasswordPolicyMessages(messages: PasswordPolicyMessages): void {
  activeMessages = messages;
}

export function getPasswordPolicyMessages(): PasswordPolicyMessages {
  return activeMessages;
}

const RULE_TESTS: Record<PasswordRuleKey, (value: string) => boolean> = {
  length: (v) => v.length >= 8,
  uppercase: (v) => /[A-Z]/.test(v),
  lowercase: (v) => /[a-z]/.test(v),
  number: (v) => /[0-9]/.test(v),
};

const RULE_ORDER: ReadonlyArray<PasswordRuleKey> = [
  "length",
  "uppercase",
  "lowercase",
  "number",
];

export interface PasswordRule {
  key: PasswordRuleKey;
  label: string;
  test: (value: string) => boolean;
}

export function getPasswordRules(
  messages: PasswordPolicyMessages = getPasswordPolicyMessages(),
): ReadonlyArray<PasswordRule> {
  return RULE_ORDER.map((key) => ({
    key,
    label: messages.rules[key].label,
    test: RULE_TESTS[key],
  }));
}

export function getPasswordPolicySummary(
  messages: PasswordPolicyMessages = getPasswordPolicyMessages(),
): string {
  return messages.summary;
}

export function isPasswordValid(value: string): boolean {
  return RULE_ORDER.every((key) => RULE_TESTS[key](value));
}

/**
 * Build a password policy schema bound to a specific message set.
 * Use this when you have a request-scoped locale (e.g. resolved from a
 * user's profile or `Accept-Language`) and want to avoid mutating the
 * process-global messages.
 */
export function buildPasswordPolicySchema(messages: PasswordPolicyMessages) {
  return z.string().superRefine((value, ctx) => {
    for (const key of RULE_ORDER) {
      if (!RULE_TESTS[key](value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.rules[key].error,
        });
      }
    }
  });
}

/**
 * Default schema that resolves messages dynamically against the
 * process-global locale at validation time. Suitable for the common
 * case where the app uses a single locale or where the locale is
 * established at bootstrap.
 */
export const passwordPolicySchema = z.string().superRefine((value, ctx) => {
  const messages = getPasswordPolicyMessages();
  for (const key of RULE_ORDER) {
    if (!RULE_TESTS[key](value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.rules[key].error,
      });
    }
  }
});
